// A jeweller: a refined little workshop with a cutting bench and faceted gems
// sparkling on the bench. Ported 1:1 from age-of-mcp's jeweller.ts: local
// walls()/gem() generators plus the shared foundationSlab/scaffold helpers.
import * as THREE from "three";
import { defineModel, benchWork, foundationSlab, scaffold, type Part } from "../../lib";

const WALL = "#a890b8";
const ROOF = "#5a4a6a";
const BENCH = "#7a5a3a";
const GEM_A = "#5ad0e0";
const GEM_B = "#e05ad0";
const GEM_C = "#9a5ae0";

function walls(h: number): Part {
  return { geometry: new THREE.BoxGeometry(0.74, h, 0.74), color: WALL, position: [0, 0.14 + h / 2, 0] };
}

function gem(x: number, z: number, color: string): Part {
  return { geometry: new THREE.OctahedronGeometry(0.06), color, position: [x, 0.14 + 0.3, z] };
}

function partsFor(s: number): Part[] {
  if (s <= 0) return [foundationSlab(0.84)];
  if (s === 1) return [foundationSlab(0.84), walls(0.3), ...scaffold(0.82, 0.6)];
  if (s === 2) return [foundationSlab(0.84), walls(0.58), ...scaffold(0.82, 1.0)];

  const wallH = 0.6, top = 0.14 + wallH, rise = 0.28, halfD = 0.44;
  const slope = Math.hypot(halfD, rise), ang = Math.atan2(rise, halfD);
  return [
    foundationSlab(0.84),
    walls(wallH),
    { geometry: new THREE.BoxGeometry(0.9, 0.06, slope), color: ROOF, position: [0, top + rise / 2, halfD / 2], rotation: [ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.9, 0.06, slope), color: ROOF, position: [0, top + rise / 2, -halfD / 2], rotation: [-ang, 0, 0] },
    { geometry: new THREE.BoxGeometry(0.6, 0.06, 0.2), color: BENCH, position: [0, 0.14 + 0.26, 0.3] },
    { geometry: new THREE.BoxGeometry(0.06, 0.26, 0.06), color: BENCH, position: [-0.25, 0.14 + 0.13, 0.3] },
    { geometry: new THREE.BoxGeometry(0.06, 0.26, 0.06), color: BENCH, position: [0.25, 0.14 + 0.13, 0.3] },
    gem(-0.16, 0.3, GEM_A),
    gem(0.02, 0.3, GEM_B),
    gem(0.18, 0.3, GEM_C),
  ];
}

export const jeweller = defineModel({
  id: "jeweller",
  tiles: { w: 2, h: 2 },
  batchId: "standard",
  name: "Jeweller",
  tags: ["building","jeweller","low-poly","age-of-mcp","classic"],
  summary: "Low-poly jeweller.",
  description: "A gem-cutter's bench that cuts raw crystals into faceted jewels.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => partsFor(0) },
    { id: "stage-1", label: "Lower", parts: () => partsFor(1) },
    { id: "stage-2", label: "Upper", parts: () => partsFor(2) },
    { id: "complete", label: "Complete", parts: () => partsFor(3) },
  ],
  animations: [],
  stations: [
    { id: "bench", position: [0, 0, 0.54], facing: Math.PI, description: "The jeweller's bench." },
  ],
  interactions: [
    { id: "work", label: "At the bench", station: "bench", variants: ["complete"], ...benchWork() },
  ],
});
